/**
 * 전자계약 스토어 - Pinia
 */
import { defineStore } from "pinia";
import axiosInstance from "@/api/axios";

export const useContractStore = defineStore("contract", {
  state: () => ({
    // 관리자
    contracts: [],
    contractDetail: null,
    templates: [],
    parties: [],
    contractLogs: [],
    contractLoading: false,
    contractTotal: 0,

    // 직원
    myContracts: [],
    myContractDetail: null,
    pendingCount: 0,
  }),

  actions: {
    // ===== 양식 관리 =====
    async fetchTemplateDefaults(category) {
      const res = await axiosInstance.get("/contract-templates/defaults/", { params: { category } });
      return res.data; // { term_fields, body_html }
    },

    async previewTemplateBody(payload) {
      const res = await axiosInstance.post("/contract-templates/preview-body/", payload, { responseType: "blob" });
      return res.data; // Blob(pdf)
    },

    async setPartyTerms(contractId, partyId, terms) {
      const res = await axiosInstance.post(`/contracts/${contractId}/set-terms/`, { party_id: partyId, terms });
      return res.data;
    },

    async generateDocuments(contractId, onlyMissing = false) {
      const res = await axiosInstance.post(`/contracts/${contractId}/generate-documents/`, null, { params: { only_missing: onlyMissing ? 1 : 0 } });
      return res.data;
    },

    async downloadTermsExcel(contractId, filename) {
      const res = await axiosInstance.get(`/contracts/${contractId}/terms-excel/`, { responseType: "blob" });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = href; a.download = filename; a.click();
      setTimeout(() => URL.revokeObjectURL(href), 10000);
    },

    async uploadTermsExcel(contractId, file, apply = false) {
      const fd = new FormData(); fd.append("file", file); if (apply) fd.append("apply", "1");
      const res = await axiosInstance.post(`/contracts/${contractId}/terms-excel/`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      return res.data;
    },

    async fetchTemplates(params = {}) {
      const res = await axiosInstance.get("/contract-templates/", { params });
      this.templates = res.data.results || res.data;
      return res.data;
    },

    async createTemplate(data) {
      const res = await axiosInstance.post("/contract-templates/", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    },

    async updateTemplate(id, data) {
      const res = await axiosInstance.patch(`/contract-templates/${id}/`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    },

    async createTemplateJSON(data) {
      const res = await axiosInstance.post("/contract-templates/", data);
      return res.data;
    },

    async updateTemplateJSON(id, data) {
      const res = await axiosInstance.patch(`/contract-templates/${id}/`, data);
      return res.data;
    },

    async deleteTemplate(id) {
      await axiosInstance.delete(`/contract-templates/${id}/`);
    },

    // 계약서 영구 삭제 (계약관리자: 작성중·취소만 / superuser: 전부)
    async deleteContract(id) {
      await axiosInstance.delete(`/contracts/${id}/`);
    },

    // 배포된 계약서가 있어 잠긴 양식 → 복사본 새 버전(기존은 비활성)
    async newTemplateVersion(id) {
      const res = await axiosInstance.post(`/contract-templates/${id}/new-version/`);
      return res.data;
    },

    // ===== 대상자 선택용 =====
    async fetchDepartments() {
      const res = await axiosInstance.get("/contracts/departments/");
      return res.data;
    },

    async fetchEmployeesList(params = {}) {
      const res = await axiosInstance.get("/contracts/employees-list/", { params });
      return res.data;
    },

    // ===== 계약서 관리 (관리자) =====
    async fetchContracts(params = {}) {
      this.contractLoading = true;
      try {
        const res = await axiosInstance.get("/contracts/", { params });
        this.contracts = res.data.results || res.data;
        this.contractTotal = res.data.count || this.contracts.length;
      } finally {
        this.contractLoading = false;
      }
    },

    async fetchContractDetail(id) {
      const res = await axiosInstance.get(`/contracts/${id}/`);
      this.contractDetail = res.data;
      return res.data;
    },

    async createContract(data) {
      const res = await axiosInstance.post("/contracts/", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    },

    async updateContract(id, data) {
      const res = await axiosInstance.patch(`/contracts/${id}/`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    },

    async distributeContract(id) {
      const res = await axiosInstance.post(`/contracts/${id}/distribute/`);
      return res.data;
    },

    async remindContract(id) {
      const res = await axiosInstance.post(`/contracts/${id}/remind/`);
      return res.data;
    },

    async cancelContract(id, reason = "") {
      const res = await axiosInstance.post(`/contracts/${id}/cancel/`, { reason });
      return res.data;
    },

    async fetchParties(contractId, params = {}) {
      const res = await axiosInstance.get(`/contracts/${contractId}/parties/`, { params });
      this.parties = res.data;
      return res.data;
    },

    async addParties(contractId, employeeIds) {
      const res = await axiosInstance.post(`/contracts/${contractId}/add_parties/`, {
        employee_ids: employeeIds,
      });
      return res.data;
    },

    async uploadIndividualFile(contractId, partyId, file) {
      const formData = new FormData();
      formData.append("party_id", partyId);
      formData.append("file", file);
      const res = await axiosInstance.post(`/contracts/${contractId}/upload-individual/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    },

    async fetchContractLogs(contractId) {
      const res = await axiosInstance.get(`/contracts/${contractId}/logs/`);
      this.contractLogs = res.data;
      return res.data;
    },

    async exportExcel(contractId) {
      const res = await axiosInstance.get(`/contracts/${contractId}/export-excel/`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      const disposition = res.headers["content-disposition"];
      const filename = disposition
        ? disposition.split("filename=")[1]?.replace(/"/g, "")
        : `contract_${contractId}_consent.xlsx`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },

    // ===== 내 계약서 (직원용) =====
    async fetchMyContracts(params = {}) {
      const res = await axiosInstance.get("/my-contracts/", { params });
      this.myContracts = res.data.results || res.data;
      return res.data;
    },

    async fetchMyContractDetail(partyId) {
      const res = await axiosInstance.get(`/my-contracts/${partyId}/`);
      this.myContractDetail = res.data;
      return res.data;
    },

    async fetchPendingCount() {
      const res = await axiosInstance.get("/my-contracts/pending_count/");
      this.pendingCount = res.data.pending_count;
      return res.data.pending_count;
    },

    async recordRead(partyId) {
      await axiosInstance.post(`/my-contracts/${partyId}/read/`);
    },

    async recordScrollComplete(partyId) {
      await axiosInstance.post(`/my-contracts/${partyId}/scroll-complete/`);
    },

    async recordClose(partyId, sessionSeconds) {
      await axiosInstance.post(`/my-contracts/${partyId}/close/`, {
        session_seconds: sessionSeconds,
      });
    },

    async submitConsent(partyId, consentItemsChecked, sessionSeconds = 0, signature = "") {
      const res = await axiosInstance.post(`/my-contracts/${partyId}/consent/`, {
        consent_items_checked: consentItemsChecked,
        session_seconds: sessionSeconds,
        signature,
      });
      return res.data;
    },

    // 서명 완성본 PDF 내려받기 (인증 경로 — 직원 교부용 / 관리자 증빙용)
    async downloadSignedDocument(url, filename) {
      const res = await axiosInstance.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = href; a.download = filename; a.click();
      setTimeout(() => URL.revokeObjectURL(href), 10000);
    },

    async submitRejection(partyId, reason, sessionSeconds = 0) {
      const res = await axiosInstance.post(`/my-contracts/${partyId}/reject/`, {
        reason,
        session_seconds: sessionSeconds,
      });
      return res.data;
    },

    async reRequestParty(contractId, partyId, note = "") {
      const res = await axiosInstance.post(`/contracts/${contractId}/re-request/`, { party_id: partyId, note });
      return res.data;
    },
  },
});
